import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import Helmet from 'react-helmet'
import classnames from 'classnames'
import stateSelector from 'reduxModules/learningPaths/selectors'
import { learningPathsStatus } from 'reduxModules/learningPaths'
import learningPathMergeProps from 'reduxModules/learningPaths/dispatchers'
import _ from 'lodash'
import 'rodal/lib/rodal.css'
import LoadingComponent from 'components/generic/LoadingComponent'
import withRouterV6 from '../../../utils/withRouterV6'

import RadialProgress from '../../generic/RadialProgress'
import LearningPathCourses from './LearningPathCourses'
import RadialProgressCategory from './RadialProgressCategory'
import Artist from '../../generic/ProfileSummary'
import SlideShow from '../../generic/SlideShow'
import VideoModal from '../../generic/VideoModal'
import getOverallDifficulty from './utils'
import SubscribeDialogCTA from '../../App/SubscribeDialogCTA'

import './styles.css'

export class LearningPathDetail extends Component {
  constructor(props) {
    super(props)
    this.state = {
      cta: '',
      filter: []
    }
  }

  componentDidMount() {
    const { getLearningPath, getAllLearningPathCourses } = this.props
    getLearningPath()
    getAllLearningPathCourses()
    const cta = this.getMainCTA()
    this.setState({ cta })
  }

  componentDidUpdate(prevProps) {
    const { params, owned, lastBookmark, isSubscriber } = this.props

    if (prevProps.params.learningPathSlug !== params.learningPathSlug) {
      this.props.getLearningPath()
      this.props.getAllLearningPathCourses()
    }

    if (
      owned !== prevProps.owned ||
      lastBookmark !== prevProps.lastBookmark ||
      isSubscriber !== prevProps.isSubscriber
    ) {
      let cta = this.getMainCTA()
      if (this.state.cta === 'enroll' && owned) cta = 'start pathway'
      this.setState({ cta })
    }
  }

  componentWillUnmount() {
    this.props.clearLearningPath()
  }

  filterCourse(course) {
    let newFilter = this.state.filter
    const index = newFilter.indexOf(course)
    if (index !== -1) newFilter.splice(index, 1)
    else newFilter = [...newFilter, course]
    this.setState({
      filter: newFilter
    })
  }

  getMainCTA() {
    const { owned, lastBookmark, isSubscriber } = this.props
    if (!isSubscriber) return 'subscribe'
    if (!owned) return 'enroll'
    if (_.isNull(lastBookmark)) return 'start pathway'
    return 'continue pathway'
  }

  onClickMainCTA() {
    const {
      owned,
      resumeWatching,
      enrollUser,
      url,
      slug,
      lastBookmark,
      courses,
      id,
      location,
      isSubscriber,
      subscription
    } = this.props
    const firstCourse = courses.length > 0 ? courses[0].slug : null // Pick first course if not started

    if (!owned) enrollUser(url, slug, isSubscriber)
    else
      resumeWatching(
        lastBookmark,
        firstCourse,
        slug,
        id,
        location.pathname,
        subscription
      )
  }

  render() {
    if (this.props.status !== learningPathsStatus.success)
      return <LoadingComponent />

    const { props } = this
    const numCategories = _.size(props.statsByCategory)
    const { owned, isSubscriber } = props
    const onTrial = props.subscription && props.subscription.onTrial
    const metaTitle = props.metaTitle || props.title
    const metaDescription = props.metaDescription || props.description
    const radialProgressSize = this.props.width / (numCategories + 3)
    const categoriesShouldScroll = numCategories > 3 && this.props.width < (numCategories * 150)
    const courseProgress = _.sumBy(props.statsByCategory, 'completed')

    return (
      <article id="LearningPathDetail">
        <Helmet>
          <title>{metaTitle} | Stan Winston School of Character Arts</title>
          <meta name="description" content={metaDescription} />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDescription} />
          {!props.facebookShareImages.length && _.has(props, 'photo.original') && (
            <meta property="og:image" content={props.photo.original} />
          )}
          {props.facebookShareImages.length > 0 && props.facebookShareImages.map((fbImage, fbImageIndex) => (
            <meta property="og:image" content={fbImage.original} key={fbImageIndex} />
          ))}
          <meta property="og:type" content="article" />
        </Helmet>

        <div className="header" style={{ backgroundImage: `url(${props.photo.original})` }}>
          <div className="overlay" />
          <div className="wrapper">
            <h1 className="title">{props.title}</h1>
            <h2 className="tagline">{props.tagline}</h2>
            {owned && (
              <div className="progress">
                <span>{`${courseProgress} course${courseProgress === 1 ? '' : 's'} completed of ${props.numTutorials}`}</span>
                {props.numTutorials > 0 && (
                  <div className="progress-line">
                    <div style={{ width: `${props.overallProgress}%` }} className="current-progress" />
                  </div>
                )}
              </div>
            )}
            {numCategories > 0 && (
              <div className={classnames('categories-progress-container', { static: !categoriesShouldScroll })}>
                {!categoriesShouldScroll && props.statsByCategory.map((progress, progressIndex) => (
                  <RadialProgressCategory
                    key={progressIndex}
                    current={progress}
                    amount={numCategories}
                    owned={owned}
                    filtrable
                    filterActive={this.state.filter.includes(progress.category)}
                    filtered={this.state.filter}
                    onClick={() => this.filterCourse(progress.category)}
                  />
                ))}
                {categoriesShouldScroll && (
                  <div className="slide-categories">
                    <SlideShow
                      arrows={false}
                      draggable={this.props.isMobile}
                      dots
                      autoPlay={false}
                      slidesToShow={this.props.isMobile ? 2 : 3}
                      slidesToScroll={1}
                    >
                      {props.statsByCategory.map((progress, progressIndex) => (
                        <div key={progressIndex}>
                          <RadialProgressCategory
                            current={progress}
                            amount={numCategories}
                            owned={owned}
                            onClick={this.filterCourse}
                          />
                        </div>
                      ))}
                    </SlideShow>
                  </div>
                )}
              </div>
            )}
            {_.size(props.courses) > 0 && (
              <div className="continue-btn">
                {!isSubscriber && (
                  <SubscribeDialogCTA>
                    <div className="btn -wide -orange wide-webkit">
                      <span>subscribe</span>
                    </div>
                  </SubscribeDialogCTA>
                )}
                {isSubscriber && (
                  <button
                    type="button"
                    className="btn -wide -blue-2"
                    onClick={() => this.onClickMainCTA()}
                  >
                    {this.state.cta}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="description wrapper-1080">
          <div className="overview">
            <section className="heading"><h1>Overview</h1></section>
            <p>{props.overview}</p>
          </div>
          <div className="info">
            <div className="difficulty">
              <i className={`icon-level${_.max(props.difficulty)}`} />
              <span>{getOverallDifficulty(props.difficulty)}</span>
            </div>
            <div className="courses-amount">
              <i className="icon-stream" />
              <span>{props.numTutorials} Courses</span>
            </div>
            <div className="duration">
              <i className="fa-clock" />
              {props.duration && <span>{props.duration[0]}h </span>}
              <span>{props.duration[1]}m</span>
              of duration
            </div>
            <hr />
            <div className="forum">
              <a href="https://forums.stanwinstonschool.com" target="_blank" rel="noopener noreferrer">
                <i className="icon icon-forum" />
                <span>Discuss on our Forums</span>
              </a>
            </div>
          </div>
        </div>

        <div className="wrapper learning-path-courses">
          <LearningPathCourses
            courses={props.courses || []}
            filter={this.state.filter}
            key={props.courses.length}
            loadLearningPathwayCourse={props.loadLearningPathwayCourse}
            loadBookmarkContext={{ location: props.location }}
            owned={props.owned}
            pathId={props.id}
            routePath={props.location.pathname}
            setVideoPreview={props.setVideoPreview}
            slug={props.slug}
            isEnrolled={props.isEnrolled}
            isBasicSubscriber={props.subscription && props.subscription.plan === 'Basic'}
          />
        </div>

        <div className="wrapper">
          <section className="heading"><h1>Teachers in this Specialization</h1></section>
          <div className="teachers">
            {props.artists.map((artist, artistIndex) => (
              <Artist
                key={`artist-${artistIndex}`}
                className="artist"
                name={artist.title}
                photo={artist.photo.boxThumb}
                slug={artist.slug}
              />
            ))}
          </div>
        </div>

        {this.props.videoPreview && (
          <VideoModal
            url={this.props.videoPreviewDetail.path}
            visible={this.props.videoPreview}
            close={() => this.props.closeVideoPreview()}
            isYoutube={this.props.videoPreviewDetail.isYoutube}
          />
        )}
      </article>
    )
  }
}

LearningPathDetail.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired
}

LearningPathDetail.defaultProps = {
  facebookShareImages: [],
  progressByCategory: []
}

export default withRouterV6(connect(
  stateSelector,
  null,
  learningPathMergeProps
)(LearningPathDetail))
